import { useCallback, useRef, type RefObject } from 'react';

import { readAnchor, type ListAnchor } from './listAnchor';
import type { Landing } from './useOpportunityFeed';

/**
 * Holds the user's place in the results list across a filter change.
 *
 * The anchor is captured while scrolling rather than at the moment the filter
 * changes, because by the time a change is observable the old list is already
 * on its way out. Capture is one hit-test per animation frame, and only while
 * the pane is actually moving.
 *
 * Restoring is driven from a layout effect in the caller, once the new first
 * page is in the DOM — after the list has been rewritten but before the
 * browser paints or dispatches the scroll event the shorter list is about to
 * trigger. That ordering is what stops the clamp-scroll from overwriting the
 * anchor we are in the middle of using.
 *
 * A reader at the top of the list is the exception: nothing is being held
 * there, so nothing should be returned to. Filtering from the top would
 * otherwise leave the first filtered event as the place to come back to, and
 * taking the filter off again would open the list at that event's moment —
 * midday on the 23rd for someone who never left the beginning of the week.
 */

/** Frames to keep re-trying before accepting that the card will not appear. */
const RESTORE_FRAMES = 60;

/**
 * How near the top still counts as the top, in pixels.
 *
 * Where the pane sits now, not how it got there: scrolling back up to the
 * beginning is the same intention as never having left it.
 */
const TOP_EPSILON = 4;

export interface Landed {
  /** The cards now shown, in order. */
  events: { id: string }[];
  at: Landing;
  /** The anchor the first page was asked for. */
  anchorId: string | null;
}

export interface ListAnchoring {
  /** Attach to the pane alongside any other scroll handler. */
  onScroll: () => void;
  /** The card being read, for the next first page to start at. */
  current: () => string | null;
  /** Put the list back where it was, against a freshly loaded first page. Call from a layout effect. */
  restore: (landed: Landed) => void;
  /** Forget the anchor — "start over" has no place to hold. */
  clear: () => void;
}

export function useListAnchor(pane: RefObject<HTMLDivElement | null>): ListAnchoring {
  const anchor = useRef<ListAnchor | null>(null);
  const capturing = useRef(0);
  const restoring = useRef(false);
  const loop = useRef(0);

  /** What the list is holding: the card in the middle, or nothing at all at the top. */
  const capture = useCallback((node: HTMLDivElement) => {
    // A list too short to scroll says nothing about where the reader wants to
    // be — there was nowhere else to go. Whatever the last list they could
    // move around in was showing still stands.
    if (node.scrollHeight - node.clientHeight <= TOP_EPSILON) return;
    anchor.current = node.scrollTop <= TOP_EPSILON ? null : readAnchor(node);
  }, []);

  const onScroll = useCallback(() => {
    // A restore scrolls the pane itself; capturing during one would overwrite
    // the anchor with the position we are still moving away from.
    if (restoring.current || capturing.current) return;

    capturing.current = requestAnimationFrame(() => {
      capturing.current = 0;
      const node = pane.current;
      if (node && !restoring.current) capture(node);
    });
  }, [capture, pane]);

  const current = useCallback(() => anchor.current?.id ?? null, []);

  const clear = useCallback(() => {
    anchor.current = null;
    restoring.current = false;
    cancelAnimationFrame(loop.current);
  }, []);

  /**
   * Waits for the card to exist, then puts it at `offset` from the pane's top
   * (or the middle, when there is no offset to honor).
   *
   * Pages loading above the card while this runs move it down; each frame
   * measures again, so it follows rather than fights them.
   */
  const settle = useCallback(
    (node: HTMLDivElement, id: string | null, offset: number | null) => {
      let frames = 0;

      const step = () => {
        if (id === null) {
          node.scrollTop = node.scrollHeight;
        } else {
          const card = node.querySelector<HTMLElement>(`[data-event-id="${CSS.escape(id)}"]`);
          if (card) {
            const paneTop = node.getBoundingClientRect().top;
            const want = offset ?? (node.clientHeight - card.offsetHeight) / 2;
            const delta = card.getBoundingClientRect().top - paneTop - want;
            if (Math.abs(delta) > 1) node.scrollTop += delta;
            else frames = RESTORE_FRAMES; // settled
          }
        }

        frames += 1;
        if (frames < RESTORE_FRAMES) {
          loop.current = requestAnimationFrame(step);
        } else {
          restoring.current = false;
          // Whatever is under the middle now is the anchor for the next change.
          capture(node);
        }
      };

      loop.current = requestAnimationFrame(step);
    },
    [capture],
  );

  const restore = useCallback(
    ({ events, at, anchorId }: Landed) => {
      const node = pane.current;
      const held = anchor.current;
      cancelAnimationFrame(loop.current);
      if (!node) return;

      // Nothing to hold onto — an untouched list, or a search that matched
      // nothing. The top is the honest answer.
      if (at === 'top' || !anchorId || events.length === 0) {
        node.scrollTop = 0;
        restoring.current = false;
        return;
      }

      restoring.current = true;

      // Past the end of the new list: nothing runs as late as where they were,
      // so show the last of what does.
      if (at === 'end') {
        settle(node, null, 0);
        return;
      }

      // An exact hit goes back to the pixel it occupied. A substitute — the
      // first event at or after that moment — is centered, since there is no
      // old position for it to return to.
      const first = events[0];
      const exact = first.id === anchorId && held?.id === anchorId;
      settle(node, first.id, exact && held ? held.offsetTop : null);
    },
    [pane, settle],
  );

  return { onScroll, current, restore, clear };
}
